import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  updateDoc,
  addDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";

// Türkçe TitleCase (boşluk ve '-' ayırıcılarını korur)
const toTitleCaseTR = (str = "") => {
  const s = str.toString().trim().toLocaleLowerCase("tr-TR");
  return s
    .split(/(\s+|-)/g)
    .map((part) => {
      if (part.trim() === "" || part === "-") return part;
      return part.charAt(0).toLocaleUpperCase("tr-TR") + part.slice(1);
    })
    .join("");
};

const trLower = (s = "") => s.toString().trim().toLocaleLowerCase("tr-TR");

const KategoriExcelYukle = () => {
  const { userPermissions } = useAuth();

  const [dosya, setDosya] = useState(null);
  const [progress, setProgress] = useState(0);
  const [hataDosyasiAktif, setHataDosyasiAktif] = useState(true);

  // Sistem sözlükleri
  const [sanayiMap, setSanayiMap] = useState(new Map()); // lower -> canonical (faturalar)
  const [eksiklikMap, setEksiklikMap] = useState(new Map()); // lower -> canonical (issues)

  // Mevcut atamalar (global) — case-insensitive
  const [mevcutAtamalarLower, setMevcutAtamalarLower] = useState(new Map()); // lower -> { original, katId, kategori, grup, altGrup }

  // Mevcut kombinasyonlar — hem orijinal hem lower içerir
  const [mevcutKombolar, setMevcutKombolar] = useState(new Map()); // key -> { id, kategori, grup, altGrup, icerik:Set, icerikLower:Set }

  useEffect(() => {
    const fetchAll = async () => {
      const [faturaSnap, issueSnap, catSnap] = await Promise.all([
        getDocs(collection(db, "faturalar")),
        getDocs(collection(db, "issues")),
        getDocs(collection(db, "categories")),
      ]);

      // 1) Ürün sözlükleri
      const sMap = new Map();
      faturaSnap.forEach((d) => {
        const satirlar = d.data().satirlar || [];
        satirlar.forEach((s) => {
          const ad = (s.urunAdi || "").toString().trim();
          if (!ad) return;
          const lower = trLower(ad);
          if (!sMap.has(lower)) sMap.set(lower, ad); // ilk görülen yazımı canonical
        });
      });

      const eMap = new Map();
      issueSnap.forEach((d) => {
        const issue = (d.data().issue || "").toString().trim();
        if (!issue) return;
        const lower = trLower(issue);
        if (!eMap.has(lower)) eMap.set(lower, issue);
      });

      setSanayiMap(sMap);
      setEksiklikMap(eMap);

      // 2) Kategoriler ve atamalar
      const combos = new Map();
      const assignmentsLower = new Map();

      catSnap.docs.forEach((docu) => {
        const data = docu.data();
        const kategori = (data.kategori || "").toString().trim();
        const grup = (data.grup || "").toString().trim();
        const altGrup = (data.altGrup || "").toString().trim();
        const icerik = Array.isArray(data.icerik) ? data.icerik : [];

        const key = `${kategori}|${grup}|${altGrup}`;
        const icerikSet = new Set(icerik);
        const icerikLowerSet = new Set(icerik.map((p) => trLower(p)));

        combos.set(key, {
          id: docu.id,
          kategori,
          grup,
          altGrup,
          icerik: icerikSet,
          icerikLower: icerikLowerSet,
        });

        icerik.forEach((p) => {
          const lower = trLower(p);
          if (!assignmentsLower.has(lower)) {
            assignmentsLower.set(lower, {
              original: p,
              katId: docu.id,
              kategori,
              grup,
              altGrup,
            });
          }
        });
      });

      setMevcutKombolar(combos);
      setMevcutAtamalarLower(assignmentsLower);
    };

    fetchAll();
  }, []);

  const hasAccess = !!(userPermissions?.fatura?.yonetici || userPermissions?.yonetici);

  const handleOrnekIndir = () => {
    const wb = XLSX.utils.book_new();
    const ornek = [
      { "Ürün Adı": "Reflektör", "Grup": "Aydınlatma", "Alt Grup": "Far Sistemi" },
      { "Ürün Adı": "Takoz", "Grup": "Yedek Parça", "Alt Grup": "Süspansiyon" },
      { "Ürün Adı": "Şaft Koruması", "Grup": "Mekanik", "Alt Grup": "" },
    ];
    const ws = XLSX.utils.json_to_sheet(ornek);
    XLSX.utils.book_append_sheet(wb, ws, "Kategori Yükleme");
    XLSX.writeFile(wb, "kategori_yukleme_ornek.xlsx");
  };

  const handleYukle = async () => {
    if (!dosya) return alert("Lütfen bir Excel dosyası seçin.");

    const reader = new FileReader();
    reader.onload = async (e) => {
      setProgress(0);
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const ws = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      const hatalar = [];
      const excelIciTekrarSet = new Set(); // ürün adı (lower) tekrarı

      // combo -> Set(urunCanonical) (toplu update)
      const eklenecekHarita = new Map();
      // otomatik oluşturulacak kombinasyonlar
      const missingComboKeys = new Set();
      const createdCombos = []; // bilgi mesajı için

      const total = rows.length || 1;
      let islenen = 0;

      for (const row of rows) {
        const rawUrun = (row["Ürün Adı"] || "").toString().trim();
        const rawGrup = (row["Grup"] || "").toString().trim();
        const rawAltGrup = (row["Alt Grup"] || "").toString().trim();

        if (!rawUrun) {
          hatalar.push({ ...row, "Hata Nedeni": "Ürün Adı eksik" });
          islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
        }
        if (!rawGrup) {
          hatalar.push({ ...row, "Hata Nedeni": "Grup adı zorunludur" });
          islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
        }

        // Excel içi tekrar kontrolü (case-insensitive)
        const lowerUrunInput = trLower(rawUrun);
        if (excelIciTekrarSet.has(lowerUrunInput)) {
          hatalar.push({ ...row, "Hata Nedeni": "Aynı dosyada ürün adı tekrar ediyor" });
          islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
        }
        excelIciTekrarSet.add(lowerUrunInput);

        // Ürün sistemde var mı? (issues veya faturalar)
        const inEksiklik = eksiklikMap.has(lowerUrunInput);
        const inSanayi = sanayiMap.has(lowerUrunInput);

        if (!inEksiklik && !inSanayi) {
          hatalar.push({ ...row, "Hata Nedeni": "Ürün sistemde tanımlı değil (issues/faturalar yok)" });
          islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
        }

        // Kategori otomatik (kaynağa göre)
        let kategori = null;
        if (inEksiklik && !inSanayi) kategori = "Eksiklik";
        else if (!inEksiklik && inSanayi) kategori = "Sanayi";
        else {
          hatalar.push({ ...row, "Hata Nedeni": "Ürün hem issues hem faturalar içinde var (kategori belirsiz)" });
          islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
        }

        // Canonical ürün adı (sistemdeki yazımı)
        const canonicalName =
          kategori === "Eksiklik"
            ? eksiklikMap.get(lowerUrunInput)
            : sanayiMap.get(lowerUrunInput);
        const lowerCanonical = trLower(canonicalName);

        // Daha önce atanmış mı? (global & case-insensitive)
        if (mevcutAtamalarLower.has(lowerCanonical)) {
          const atama = mevcutAtamalarLower.get(lowerCanonical);
          hatalar.push({
            ...row,
            "Hata Nedeni": `Ürün zaten atanmış (${atama.original} → ${atama.kategori} / ${atama.grup}${
              atama.altGrup ? " / " + atama.altGrup : ""
            })`,
          });
          islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
        }

        // Grup/Alt Grup normalize
        const grup = toTitleCaseTR(rawGrup);
        const altGrup = rawAltGrup ? toTitleCaseTR(rawAltGrup) : "";

        // Kombinasyon anahtarı
        const comboKey = `${kategori}|${grup}|${altGrup}`;
        const combo = mevcutKombolar.get(comboKey);

        if (combo) {
          // Comboda zaten var mı? (case-insensitive)
          if (combo.icerikLower.has(lowerCanonical)) {
            hatalar.push({
              ...row,
              "Hata Nedeni": `Ürün zaten bu grupta kayıtlı (${canonicalName})`,
            });
            islenen++; setProgress(Math.round((islenen / total) * 100)); continue;
          }
        } else {
          // ❗ Yoksa: otomatik oluşturulacaklar listesine ekle
          missingComboKeys.add(comboKey);
        }

        // Ekleme havuzu (combo var/yok fark etmeksizin topla)
        if (!eklenecekHarita.has(comboKey)) {
          eklenecekHarita.set(comboKey, new Set());
        }
        eklenecekHarita.get(comboKey).add(canonicalName);

        islenen++; setProgress(Math.round((islenen / total) * 100));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 0));
      }

      // 🔧 Eksik kombinasyonları Firestore'da oluştur
      if (missingComboKeys.size > 0) {
        for (const comboKey of missingComboKeys) {
          const [kategori, grup, altGrup] = comboKey.split("|");
          const ref = await addDoc(collection(db, "categories"), {
            kategori,
            grup,
            altGrup,
            icerik: [],
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser?.email || "excel-import",
          });
          // yerel cache'i güncelle
          mevcutKombolar.set(comboKey, {
            id: ref.id,
            kategori,
            grup,
            altGrup,
            icerik: new Set(),
            icerikLower: new Set(),
          });
          createdCombos.push({ kategori, grup, altGrup });
        }
      }

      // 🔄 Firestore güncellemeleri (komboya göre toplu ekleme) — case-insensitive merge
      for (const [comboKey, urunSet] of eklenecekHarita.entries()) {
        const combo = mevcutKombolar.get(comboKey);
        if (!combo) continue; // güvenlik

        // Mevcutları lower->original map'ine topla
        const lowerToOriginal = new Map();
        combo.icerik.forEach((name) => lowerToOriginal.set(trLower(name), name));

        // Yeni ürünleri ekle (yalnızca lower yoksa)
        urunSet.forEach((name) => {
          const lower = trLower(name);
          if (!lowerToOriginal.has(lower)) {
            lowerToOriginal.set(lower, name); // yeni canonical yazım
          }
        });

        const yeniIcerik = Array.from(lowerToOriginal.values());
        const ref = doc(db, "categories", combo.id);

        await updateDoc(ref, {
          icerik: yeniIcerik,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || "excel-import",
        });

        // Lokal cache'i de güncelle (bir sonraki satır için)
        combo.icerik = new Set(yeniIcerik);
        combo.icerikLower = new Set(yeniIcerik.map((n) => trLower(n)));
      }

      // ✅ Başarı sayısı (eklenecekHarita'daki Set'lerin toplamı)
      const basariliSayisi = Array.from(eklenecekHarita.values()).reduce(
        (toplam, set) => toplam + set.size,
        0
      );

      const createdInfo =
        createdCombos.length > 0
          ? `\n\nOluşturulan yeni grup/alt gruplar:\n- ${createdCombos
              .map(
                (c) =>
                  `${c.kategori} / ${c.grup}${c.altGrup ? " / " + c.altGrup : ""}`
              )
              .join("\n- ")}`
          : "";

      alert(
        `Kategori toplu yükleme tamamlandı ✅\n\n` +
          `Toplam satır sayısı: ${rows.length}\n` +
          `Başarıyla yüklenen satır sayısı: ${basariliSayisi}` +
          `${createdCombos.length > 0 ? createdInfo : ""}`
      );

      setProgress(0);

      // Hata dosyası
      if (hataDosyasiAktif && hatalar.length > 0) {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(hatalar);
        XLSX.utils.book_append_sheet(wb, ws, "Hatalar");
        XLSX.writeFile(wb, "kategori_yukleme_hatalari.xlsx");
      }
    };

    reader.readAsBinaryString(dosya);
  };

  if (!hasAccess) {
    return (
      <div style={{ padding: 20 }}>
        <h2 style={{ fontSize: "1.2rem", fontWeight: "bold", color: "red" }}>
          Erişiminiz yok.
        </h2>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "auto" }}>
      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          color: "#1e40af",
          marginBottom: 12,
        }}
      >
        Excel ile Kategori Atama (Oto-Kategori)
      </h2>

      <div
        style={{
          padding: 12,
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          background: "#fff",
          marginBottom: 16,
        }}
      >
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          <strong>Kurallar</strong>: Ürün adı sistemde (uygunsuzluklar veya faturalar) tanımlı olmalı. Excel’de aynı ürün
          tekrarlanmamalı (büyük/küçük harf farkı gözetilmez). Ürün daha önce bir kategoriye atanmışsa eklenmez.
          <br />
          <strong>Kategori otomatik</strong>: Ürün <em>uygunsuzluk</em>’tan geliyorsa <code>Eksiklik</code>,{" "}
          <em>faturalar</em>’dan geliyorsa <code>Sanayi</code>. Her iki kaynakta da bulunursa hata verilir.
          <br />
          Grup zorunlu, Alt Grup isteğe bağlıdır. Grup/Alt Grup adları <em>Türkçe Baş Harf Büyük</em> normalize edilir.
          <br />
          <strong>Not</strong>: Dosyada belirtilen <em>Grup/Alt Grup</em> kombinasyonu sistemde yoksa otomatik
          oluşturulur ve bilgi verilir. Kayıtlar benzersizleştirilir.
        </p>
      </div>

      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => setDosya(e.target.files[0])}
        style={{ marginBottom: 10 }}
      />

      <div style={{ marginBottom: 12 }}>
        <label>
          <input
            type="checkbox"
            checked={hataDosyasiAktif}
            onChange={(e) => setHataDosyasiAktif(e.target.checked)}
          />{" "}
          Hata Dosyası Oluştur
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={handleYukle}
          style={{
            backgroundColor: "#16a34a",
            color: "white",
            padding: "8px 16px",
            borderRadius: 6,
            border: "none",
          }}
        >
          Yükle
        </button>
        <button
          onClick={handleOrnekIndir}
          style={{
            backgroundColor: "#2563eb",
            color: "white",
            padding: "8px 16px",
            borderRadius: 6,
            border: "none",
          }}
        >
          Örnek Şablon İndir
        </button>
      </div>

      {progress > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              backgroundColor: "#e5e7eb",
              height: 20,
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: "#1e40af",
                transition: "width 0.2s",
              }}
            />
          </div>
          <p style={{ marginTop: 6 }}>{progress}% tamamlandı...</p>
        </div>
      )}
    </div>
  );
};

export default KategoriExcelYukle;
